// poScanApi.ts — the contract module for scan-to-stock.
//
// Same convention as poApi.ts and poImportApi.ts: every API-shape assumption for
// this flow lives here, read off api/src/procurement/po-scan-service.js. Money
// is integer cents; formatCents in poApi.ts is the one place it becomes dollars.
//
// The STAGED shapes are not redefined here — scan-parse returns exactly the
// document shape the import lane stages (it is the same parser), so those types
// are imported from poImportApi and re-exported for readers of this module.

import { useApi, apiFetchAuthed } from '../apiHelpers';
import type {
  ImportSource,
  StagedDocument,
  StagedExtraction,
  ImportConfidence,
  ImportWarning,
  ImportLineType,
  ImportStatus,
} from './poImportApi';
import type { ConfirmReviewInput } from '../components/review/grounding';
import type { MaterialType, CommodityClass, ReceivedAs } from '../Receiving/types';

export type { StagedDocument, StagedExtraction } from './poImportApi';

export interface ScanParseResponse {
  instance: 'firefly' | 'swarm';
  instance_po_prefix: string;
  today: string;
  document: StagedDocument | null;
  /**
   * Highest numeric tail already taken per SKU stem, e.g. { 'FF-SCR-02': 4 }.
   * The SKU formula lives in Parts/skuGrammar.ts; this is the one fact it cannot
   * work out on its own, and without it every scan would propose FF-SCR-02-1.
   */
  sku_tail_max: Record<string, number>;
  /**
   * The holding-cell row this read was staged as (migration 017), so the review
   * can happen at a desk later. Null when the read produced nothing reviewable,
   * or when the stage failed — the flow still works, just without the net.
   */
  draft_id?: number | null;
}

// ── THE HOLDING CELL ────────────────────────────────────────────────────────
// Photograph at the bench, review at the desk. Every successful scan-parse
// leaves one of these behind; confirming clears it and discarding throws it
// away. The list is COMMUNAL — receiving is a team activity, and the person who
// pressed the shutter is routinely not the person who reviews it.

/** One row of the "waiting for review" list. Everything here is for display. */
export interface ScanDraftSummary {
  id: number;
  created_at: string;
  /** Null when the account that snapped it is gone (created_by ON DELETE SET NULL). */
  created_by_name: string | null;
  source: ImportSource;
  vendor_guess: string | null;
  po_number_guess: string | null;
  page_count: number;
  total_bytes: number;
}

/** One page of the original upload. The BYTES come from draftPageUrl, not here. */
export interface ScanDraftFile {
  page_number: number;
  mimetype: string;
  size_bytes: number;
}

export interface ScanDraft extends Omit<ScanDraftSummary, 'page_count' | 'total_bytes'> {
  /** The parse response VERBATIM — the review screen hydrates straight from it. */
  staged_doc: ScanParseResponse;
  files: ScanDraftFile[];
}

/** Everything the review surface will render as a page thumbnail. */
export const isDraftImage = (mimetype: string): boolean => mimetype.startsWith('image/');

/**
 * A new catalog row this document is asking to create. Both axes required.
 *
 * `mpn` and `manufacturer` are OPTIONAL and are the reviewer's to clear. They
 * were quote-lane-only until 2026-08-07, which meant every SKU a scanned purchase
 * order minted had nobody's name on it. Validated in one place now
 * (PoScanService._validateMints), which is where the reasoning lives too.
 */
export interface ScanMintInput {
  /**
   * WHICH SORT OF THING, and therefore which code the server makes.
   *
   * Required on every mint. A `short` kind arrives with NO `sku` and the server
   * assigns `STEM-XXXX` inside the confirm transaction; a `structured` kind
   * arrives with the composed board SKU. Refusals: `mint_identity_required`,
   * `mint_invalid_kind_key`, `mint_name_too_long`; a database-level failure
   * comes back as `mint_failed` with `reason` and `line_number`.
   */
  kind_key: string;
  /** What people call it, <= 60 characters. Required for a short kind, which
   *  has no code that says anything; null for a board, which generates its own
   *  name from its segments. */
  name?: string | null;
  /** A BOARD ONLY. Absent means "assign one", which is what a short kind wants. */
  sku?: string;
  material_type: MaterialType;
  commodity_class: CommodityClass;
  description?: string | null;
  mpn?: string | null;
  manufacturer?: string | null;
}

export interface ScanConfirmLineInput {
  description: string | null;
  quantity: number;
  line_type: ImportLineType;
  unit_cost_cents: number | null;
  /** Echoed only for a price that did not land on a whole cent, so the server
   *  can note the rounding on the line. Same contract as the import lane. */
  unit_price_text?: string | null;
  unit_price_exact?: boolean;
  /** Set when this line matched an item that already exists. */
  part_catalog_id?: number | null;
  /** Set when this line is minting a new item. Never both. */
  mint?: ScanMintInput | null;
  included?: boolean;
}

export interface ScanConfirmVendorCreate {
  name: string;
  type: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  payment_terms?: string | null;
}

export interface ScanConfirmDocumentInput {
  source_filename: string;
  source: ImportSource;
  po_number: string;
  /**
   * Sent for honesty, not for effect: scan-confirm PINS the status to
   * 'submitted' server-side. A scanned order was placed before its box could
   * arrive, and the receive is what moves the header on from there.
   */
  status: ImportStatus;
  order_date: string;
  quote_number?: string | null;
  ship_date?: string | null;
  ship_via?: string | null;
  terms?: string | null;
  ship_to_name?: string | null;
  ship_to_attn?: string | null;
  ship_to_address?: string | null;
  notes?: string | null;
  /**
   * The subtotal the DOCUMENT prints. Omitting it makes the server compute one
   * from the lines, which silently replaces the paper figure with our
   * arithmetic and records no disagreement.
   */
  subtotal_cents?: number;
  vendor_id?: string | null;
  vendor_create?: ScanConfirmVendorCreate | null;
  vendor_name?: string | null;
  lines: ScanConfirmLineInput[];
  attach_line_ids: string[];
  /**
   * "Yes, this really is the other product's purchase order."
   *
   * Same flag and same server check as the import lane (po-import-service.js
   * _validateDocument, which this lane calls). It matters MORE here: this lane
   * receives stock in the same request, so a box goes on a shelf against that
   * order the moment it is confirmed.
   */
  foreign_instance_ack?: boolean;
  // AI-read documents only (pdf_ai and photo_ai), and echoed for the same
  // reason as the import lane: the server recomputes the needs-review list from
  // these three rather than trusting a client-supplied one, so a payload cannot
  // shorten its own homework.
  staged_extraction?: StagedExtraction;
  staged_confidence?: ImportConfidence | null;
  staged_warnings?: ImportWarning[];
  reviews?: ConfirmReviewInput[];
}

/** Why a line was not received. Null means it was. */
export type ScanSkipReason = 'not_stock' | 'no_item' | 'goods_not_in_hand' | null;

export interface ScanConfirmLineResult {
  line_number: number;
  purchase_order_line_id: string | null;
  part_catalog_id: number | null;
  sku: string | null;
  description: string | null;
  quantity: number;
  line_type: ImportLineType;
  minted: boolean;
  received: boolean;
  /** The box the receive created; `barcode` is its sticker. */
  receipt_box_id: string | null;
  /** The stock row inside that box. */
  receipt_item_id: string | null;
  barcode: string | null;
  skipped_reason: ScanSkipReason;
  error: string | null;
  error_message: string | null;
}

/**
 * PARTIAL SUCCESS IS A REAL ANSWER HERE. The purchase order, the minted items
 * and the lines commit together; the receives run one at a time afterwards. If
 * one fails, everything already committed stays and `lines` says which landed.
 * Read `partial_receive` before writing a success message.
 */
export interface ScanConfirmResult {
  ok: true;
  purchase_order_id: string;
  po_number: string;
  vendor_id: string;
  vendor_name: string | null;
  goods_in_hand: boolean;
  minted: { line_number: number; part_catalog_id: number; sku: string }[];
  lines: ScanConfirmLineResult[];
  received_line_count: number;
  failed_line_count: number;
  partial_receive: boolean;
}

export function usePoScanApi() {
  const api = useApi();

  return {
    /**
     * Read one purchase order. `files` is a single .pdf/.xlsx, or up to eight
     * photos of the pages of ONE order.
     *
     * Every page goes up under the SAME field name, in array order: the server
     * treats a repeated `file` field as the pages of one document, and page
     * order is upload order. Sending them as file1/file2/... or as separate
     * requests would produce one purchase order per photo.
     */
    parse: (files: File | File[]): Promise<ScanParseResponse> => {
      const fd = new FormData();
      for (const file of Array.isArray(files) ? files : [files]) fd.append('file', file);
      return api.post('/procurement/po-import/scan-parse', { body: fd });
    },

    /**
     * `draftId` is the holding-cell row this review came from. The server
     * clears it on success and leaves it alone on failure, so a rejected
     * confirm keeps the scan exactly where it was.
     *
     * `countsAffirmed` is B7's control: the server refuses to STOCK
     * (goods_in_hand true) unless the caller states a human compared the
     * quantities against the physical box. Paperwork-only confirms ignore it.
     *
     * `receivedAs` names who put the boxes on the shelf (name plus PIN, see
     * api/src/parts/received-as.js). Only a stocking confirm carries it.
     */
    confirm: (
      document: ScanConfirmDocumentInput,
      goodsInHand: boolean,
      draftId: number | null = null,
      countsAffirmed = false,
      receivedAs: ReceivedAs | null = null
    ): Promise<ScanConfirmResult> =>
      api.post('/procurement/po-import/scan-confirm', {
        body: {
          document,
          goods_in_hand: goodsInHand,
          counts_affirmed: countsAffirmed,
          ...(draftId ? { draft_id: draftId } : {}),
          ...(receivedAs ? { received_as: receivedAs } : {}),
        },
      }),

    /** Everything waiting for review, oldest first. Communal. */
    listDrafts: (): Promise<{ drafts: ScanDraftSummary[] }> =>
      api.get('/procurement/po-import/scan-drafts'),

    /** One draft, with the staged document the review surface hydrates from. */
    getDraft: (id: number): Promise<ScanDraft> =>
      api.get(`/procurement/po-import/scan-drafts/${id}`),

    /** Throw a mis-snap away. Its pages go with it. */
    discardDraft: (id: number): Promise<void> =>
      api.delete(`/procurement/po-import/scan-drafts/${id}`, { skipJsonParse: true }),
  };
}

/**
 * An object URL for one page of a staged scan, or null.
 *
 * NOT an <img src> pointed at the endpoint: the file route rides the same
 * Authorization header as everything else, and a bare img tag cannot send one.
 * Same pattern as ReceiptPeek — fetch the raw Response, take the blob, mint a
 * URL. THE CALLER OWNS THE REVOKE; leaking these pins every page ever viewed in
 * memory for the life of the tab.
 */
export async function draftPageUrl(
  draftId: number,
  page: number,
  // The quote lane keeps its drafts in the SAME table behind a `kind`
  // discriminator (migration 018) and serves them from its own routes, which
  // scope by kind. Only the path differs, so it passes one in rather than
  // getting a second copy of the blob-fetch-and-revoke dance below — that dance
  // is the part that leaks memory when it exists twice and one copy is wrong.
  basePath: string = '/procurement/po-import/scan-drafts'
): Promise<string | null> {
  try {
    const resp = (await apiFetchAuthed(
      `${basePath}/${draftId}/files/${page}`,
      { skipJsonParse: true, silent: true }
    )) as Response;
    if (!resp.ok) return null;
    return URL.createObjectURL(await resp.blob());
  } catch {
    // A page that will not load is not a reason to lose the review. The screen
    // shows the reading with a note in place of the thumbnail.
    return null;
  }
}
